import Cookies from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags';
import type { Perimeter } from '@atlassian/atl-context';

import { type CategorizedStorageControlsCache, CategoryStorageType } from '../../../types';
import { CacheLoader } from '../load-storage-controls-data';
import { Logger } from '../logger';

let analyticsEnabledGlobalState: boolean | undefined;
let productGlobalState: string | undefined;
let perimeterGlobalState: Perimeter | undefined;
let webStorageControlsEnabledGlobalState: boolean | undefined;
let indexedDbControlsEnabledGlobalState: boolean | undefined;
let localConsentModeGlobalState: boolean | undefined;
let packageDebugModeEnabledGlobalState: boolean | undefined;
let allowUncategorizedCookiesInOverridesGlobalState: boolean | undefined;
let hasInitialized: boolean = false;

let cookiesCacheLoader: CacheLoader | undefined;
let localStorageCacheLoader: CacheLoader | undefined;
let sessionStorageCacheLoader: CacheLoader | undefined;
let indexedDBCacheLoader: CacheLoader | undefined;

let loadCache: (
	storageType: CategoryStorageType,
) => Promise<CategorizedStorageControlsCache | undefined> = async (
	storageType: CategoryStorageType,
) => {
	switch (storageType) {
		case CategoryStorageType.Cookies:
			return await cookiesCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.LocalStorage:
			return await localStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.SessionStorage:
			return await sessionStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.IndexedDB:
			return await indexedDBCacheLoader?.loadStorageControlsData();
		default:
			return undefined;
	}
};

interface InitializeBscGlobalStateParams {
	// Common params
	analyticsEnabled?: boolean;
	product?: string;
	perimeter?: Perimeter;
	localConsentMode?: boolean;

	// InitializeControls only
	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	allowUncategorizedCookiesInOverrides?: boolean;
}

interface OverrideBscGlobalStateParams {
	loadCache?: typeof loadCache;
	product?: typeof productGlobalState;
}

export interface BscGlobalState {
	analyticsEnabled?: boolean;
	localConsentMode?: boolean;
	product?: string;
	perimeter?: Perimeter;

	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	allowUncategorizedCookiesInOverrides?: boolean;

	packageDebugMode?: boolean;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	hasInitialized?: boolean;

	loadCache: (
		storageType: CategoryStorageType,
	) => Promise<CategorizedStorageControlsCache | undefined>;
}

export const getBscGlobalState: () => BscGlobalState = () => {
	return {
		analyticsEnabled: analyticsEnabledGlobalState ?? true,
		product: productGlobalState,
		perimeter: perimeterGlobalState,
		webStorageEnabled: webStorageControlsEnabledGlobalState ?? false,
		indexedDbEnabled: indexedDbControlsEnabledGlobalState ?? false,
		localConsentMode: localConsentModeGlobalState ?? false,
		packageDebugMode: packageDebugModeEnabledGlobalState,
		allowUncategorizedCookiesInOverrides: allowUncategorizedCookiesInOverridesGlobalState ?? false,
		cookiesCacheLoader,
		localStorageCacheLoader,
		sessionStorageCacheLoader,
		indexedDBCacheLoader,
		loadCache,
		hasInitialized,
	};
};

export const initializeGlobalState = ({
	webStorageEnabled,
	indexedDbEnabled,
	allowUncategorizedCookiesInOverrides,
	localConsentMode = false,
	product = 'all',
	perimeter = 'commercial',
	analyticsEnabled = true,
}: InitializeBscGlobalStateParams = {}): void => {
	Logger.debug('Initialize global state invoked');

	// If the app provides a value for product only in one place,
	// we want that value to be sticky and to override the default if necessary
	if (
		(!productGlobalState || productGlobalState == null || productGlobalState === 'all') &&
		product
	) {
		productGlobalState = product;
	}

	// Treating webStorageEnabled, indexedDbEnabled, and allowUncategorizedCookiesInOverrides differently
	// because they are only passed through initializeControls and
	// we don't want it to be defaulted to false via a call from the ConsentBanner
	if (webStorageEnabled !== undefined && webStorageControlsEnabledGlobalState === undefined) {
		webStorageControlsEnabledGlobalState =
			webStorageEnabled &&
			!fg('platform_moonjelly_bsc_master_killswitch') &&
			!fg('platform_moonjelly_bsc_web_storage_killswitch');
	}

	if (indexedDbEnabled !== undefined && indexedDbControlsEnabledGlobalState === undefined) {
		indexedDbControlsEnabledGlobalState =
			indexedDbEnabled &&
			!fg('platform_moonjelly_bsc_master_killswitch') &&
			!fg('platform_moonjelly_bsc_web_storage_killswitch');
	}

	if (
		allowUncategorizedCookiesInOverrides !== undefined &&
		allowUncategorizedCookiesInOverridesGlobalState === undefined
	) {
		allowUncategorizedCookiesInOverridesGlobalState = allowUncategorizedCookiesInOverrides;
	}

	// All other state can only be initialized once and is defaulted if not passed on the first initialization
	if (hasInitialized) {
		return;
	}

	if (!fg('fedramp-cookies-controls')) {
		localConsentModeGlobalState = localConsentMode;
	} else {
		// Force local consent mode when in the FedRAMP Moderate perimeter because ConsentHub is
		// not hydrated in the perimeter as of this comment
		localConsentModeGlobalState = localConsentMode || perimeter === 'fedramp-moderate';
	}

	analyticsEnabledGlobalState = analyticsEnabled;

	perimeterGlobalState = perimeter;

	cookiesCacheLoader = new CacheLoader(CategoryStorageType.Cookies, perimeter);
	localStorageCacheLoader = new CacheLoader(CategoryStorageType.LocalStorage, perimeter);
	sessionStorageCacheLoader = new CacheLoader(CategoryStorageType.SessionStorage, perimeter);
	indexedDBCacheLoader = new CacheLoader(CategoryStorageType.IndexedDB, perimeter);

	if (fg('platform_moonjelly_bsc_debug_mode')) {
		packageDebugModeEnabledGlobalState = Boolean(Cookies.get('atl-bsc-debug-mode'));
	}
	hasInitialized = true;
};

export const testing_overrideGlobalState = ({
	loadCache: overrideLoadCache,
	product: overrideProduct,
}: OverrideBscGlobalStateParams): void => {
	if (overrideLoadCache) {
		loadCache = overrideLoadCache;
	}
	if (overrideProduct) {
		productGlobalState = overrideProduct;
	}
};

export const clearGlobalState = (): void => {
	analyticsEnabledGlobalState = undefined;
	productGlobalState = undefined;
	webStorageControlsEnabledGlobalState = undefined;
	indexedDbControlsEnabledGlobalState = undefined;
	localConsentModeGlobalState = undefined;
	packageDebugModeEnabledGlobalState = undefined;
	allowUncategorizedCookiesInOverridesGlobalState = undefined;

	cookiesCacheLoader = undefined;
	localStorageCacheLoader = undefined;
	sessionStorageCacheLoader = undefined;
	indexedDBCacheLoader = undefined;
	hasInitialized = false;
};
